// src/utils/notification/threadNotificationHandler.js
import { useNavigate } from "react-router-dom";

/**
 * Thread notification handler for processing thread-related notifications
 * and handling navigation to specific threads/messages
 */
export class ThreadNotificationHandler {
  constructor(navigate) {
    this.navigate = navigate;
  }

  /**
   * Handle thread notification and navigate to appropriate location
   * @param {Object} notification - The notification object
   */
  handleThreadNotification(notification) {
    const { type, data } = notification;

    console.log("🔔 Processing thread notification:", {
      type,
      data: {
        threadId: data.threadId,
        messageId: data.messageId,
        replyId: data.replyId,
        metadata: data.metadata,
      },
    });

    switch (type) {
      case "message.liked":
        return this.handleMessageLiked(data);
      case "message.replied":
        return this.handleMessageReplied(data);
      default:
        console.warn("🚨 Unknown thread notification type:", type);
        return false;
    }
  }

  /**
   * Handle message liked notification
   * @param {Object} data - Notification data
   */
  handleMessageLiked(data) {
    const { threadId, messageId, metadata } = data;
    const fightId = metadata?.fightId;

    console.log("👍 Handling message liked notification:", {
      threadId,
      messageId,
      fightId,
      metadata,
    });

    if (!threadId) {
      console.error("❌ No threadId in message liked notification");
      return false;
    }

    // Navigate to thread with message highlight and fight context
    this.navigateToThread(threadId, messageId, null, fightId);
    return true;
  }

  /**
   * Handle message replied notification
   * @param {Object} data - Notification data
   */
  handleMessageReplied(data) {
    const { threadId, messageId, replyId, metadata } = data;
    const fightId = metadata?.fightId;

    console.log("💬 Handling message replied notification:", {
      threadId,
      messageId,
      replyId,
      fightId,
      metadata,
    });

    if (!threadId) {
      console.error("❌ No threadId in message replied notification");
      return false;
    }

    // Navigate to thread with reply highlight and fight context
    this.navigateToThread(threadId, messageId, replyId, fightId);
    return true;
  }

  /**
   * Navigate to specific thread with optional message/reply highlighting
   * @param {string} threadId - Thread ID to navigate to
   * @param {string} messageId - Optional message ID to highlight
   * @param {string} replyId - Optional reply ID to highlight
   * @param {number} fightId - Optional fight ID for context
   */
  navigateToThread(threadId, messageId = null, replyId = null, fightId = null) {
    let path = `/threads/${threadId}`;

    // Add query parameters for highlighting specific content
    const params = new URLSearchParams();
    if (messageId) {
      params.append("messageId", messageId);
    }
    if (replyId) {
      params.append("replyId", replyId);
    }
    if (fightId) {
      params.append("fightId", fightId);
    }

    if (params.toString()) {
      path += `?${params.toString()}`;
    }

    this.navigate(path);
  }

  /**
   * Navigate to lineup page for a specific event
   * @param {string} eventId - Event ID to navigate to
   */
  navigateToLineup(eventId = null) {
    if (eventId) {
      this.navigate(`/events/${eventId}/lineups`);
    } else {
      // If no eventId, navigate to events page
      this.navigate("/events");
    }
  }

  /**
   * Check if notification is thread-related
   * @param {Object} notification - Notification object
   * @returns {boolean} - True if thread-related
   */
  static isThreadNotification(notification) {
    const threadTypes = ["message.liked", "message.replied"];
    return threadTypes.includes(notification.type);
  }

  /**
   * Check if notification is fighter cancellation
   * @param {Object} notification - Notification object
   * @returns {boolean} - True if fighter cancellation
   */
  static isFighterCancellationNotification(notification) {
    return notification.type === "fighter_cancelled";
  }

  /**
   * Get thread notification display text
   * @param {Object} notification - Notification object
   * @returns {Object} - Display text object with title and message
   */
  static getThreadNotificationText(notification) {
    const { type, data } = notification;
    const actorName = data.metadata?.actorName || "Someone";
    const threadTitle = data.metadata?.threadTitle || "a discussion";

    switch (type) {
      case "message.liked":
        return {
          title: "Message Liked",
          message: `${actorName} liked your message in "${threadTitle}"`,
          preview: data.metadata?.messagePreview || "",
        };
      case "message.replied":
        return {
          title: "New Reply",
          message: `${actorName} replied to your message in "${threadTitle}"`,
          preview: data.metadata?.replyPreview || "",
        };
      case "fighter_cancelled":
        const { fighterName, eventName } = data.metadata || {};
        return {
          title: "Fight Cancelled",
          message: `${fighterName}'s fight has been cancelled for ${eventName}. They will score 0 points.`,
          preview: "",
        };
      default:
        return {
          title: "Thread Notification",
          message: `${actorName} interacted with your content`,
          preview: "",
        };
    }
  }
}

/**
 * React hook for thread notification handling
 */
export const useThreadNotificationHandler = () => {
  const navigate = useNavigate();

  const handler = new ThreadNotificationHandler(navigate);

  return {
    handleThreadNotification: (notification) =>
      handler.handleThreadNotification(notification),
    navigateToThread: (threadId, messageId, replyId) =>
      handler.navigateToThread(threadId, messageId, replyId),
    navigateToLineup: (eventId) => handler.navigateToLineup(eventId),
    isThreadNotification: ThreadNotificationHandler.isThreadNotification,
    isFighterCancellationNotification:
      ThreadNotificationHandler.isFighterCancellationNotification,
    getThreadNotificationText:
      ThreadNotificationHandler.getThreadNotificationText,
  };
};
